import React, { createContext, useState, useEffect, useContext } from "react";
import { Activity } from "../types/activity.types";
import { getActivities } from "../services/activityServices";
import useCatalogFetch from "../hooks/useCatalogFetch";
/**
* Shape of the Activities Context value.
*/
interface ActivitiesContextType {
    /** Array of activities fetched from the backend */
    activities: Activity[];
    /** Loading flag indicating if the activities are being fetched */
    loading: boolean;
    /** Error message if loading fails, otherwise null */
    error: string | null;
    reloadActivities: () => void
}

/**
* React context to provide activities data, loading status, and error state.
*/
const ActivitiesContext = createContext<ActivitiesContextType>({
    activities: [],
    loading: true,
    error: null,
    reloadActivities: () => {}
});

/**
* Provider component that fetches activities from the backend,
* manages loading and error state, and provides this data to children.
* 
* @param children - React child elements
*/
export const ActivitiesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [activities, setactivities] = useState<Activity[]>([]); //Activities
    const [loading, setLoading] = useState(true); // Loading state 
    const [error, setError] = useState<string | null>(null); // Errors
    const { reload: reloadActivities } = useCatalogFetch<Activity>({
        fetcher: getActivities,
        onData: setactivities,
        setLoading,
        setError,
        logLabel: "activity",
    });

    useEffect(() => {
        reloadActivities();
    }, [reloadActivities]);

    return (
        <ActivitiesContext.Provider value={{ activities, loading, error, reloadActivities }}>
            {children}
        </ActivitiesContext.Provider>
    );
};
/**
* Hook to access the ActivitiesContext.
* 
* @returns ActivitiesContextType object with activities, loading flag, and error message
*/
export const useActivities = () => useContext(ActivitiesContext);
